import {
  DexieStorageProvider,
} from "../platform/db/index.js";
import {
  createPreset,
  createPersona,
  type Settings,
  type StorageProvider,
  warmRoqeetCompiler,
} from "@roqeet/core";

/**
 * Single app-wide StorageProvider instance (decision #3). Everything reaches
 * persistence through this; it's typed as the core interface so no UI code
 * depends on Dexie directly (decision #10).
 */
export const storage: StorageProvider = new DexieStorageProvider();

/** Default settings for a brand-new install (decisions #8/#14/#15). */
function defaultSettings(): Settings {
  return {
    id: "global",
    theme: "dark",
    locale: "ko",
    identityTier: "anonymous", // decision #8: anonymous by default
    sync: {
      enabled: false,
      auto: true,
      debounceMs: 10000,
      includeKeys: false, // decision #15: keys excluded from sync by default
    },
    security: {
      passphraseEnabled: false,
      autoLockMinutes: 0,
    },
    performance: {
      incrementalMarkdown: false, // decision #14: safe default off
      streamFlushFps: 30,
      forceNonStreaming: false,
      reducedMotion: false,
      listOverscan: 6,
    },
    schemaVersion: 1,
  };
}

/**
 * Open the DB and seed first-run defaults (settings, a starter preset and
 * persona) so the app is usable immediately offline (decision #1 local-first).
 */
export async function bootstrap(): Promise<Settings> {
  await storage.open();
  warmRoqeetCompiler({ thread: "main", invalidationReason: "bootstrap_warmup" });

  let settings = await storage.getSettings();
  if (!settings) {
    settings = defaultSettings();
    await storage.putSettings(settings);

    // Seed a default generation preset (decision #12 staged pipeline).
    const preset = createPreset({ name: "Default" });
    await storage.presets.put(preset);

    // Seed a default user persona (decision #22 sender model).
    const persona = createPersona({ name: "You" });
    await storage.personas.put(persona);

    settings = {
      ...settings,
      defaultPresetId: preset.id,
      defaultPersonaId: persona.id,
    };
    await storage.putSettings(settings);
  }

  return settings;
}
